import { useCallback, useEffect, useRef, useState } from "react";
import { decodeNdefRecord, TagRead, tagFromNdefRecords } from "../utils/readTag";

/**
 * Web NFC: Chrome on Android only, over https, after a tap. Reading keeps running until `stop`;
 * each tag held to the phone shows up as `last`.
 */
export function useNfcScan() {
  const supported = typeof window !== "undefined" && "NDEFReader" in window;
  const [last, setLast] = useState<TagRead>();
  const [error, setError] = useState<string>();
  const [scanning, setScanning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setScanning(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported || !window.NDEFReader) return;
    stop();
    setError(undefined);
    const controller = new AbortController();
    abortRef.current = controller;
    const reader = new window.NDEFReader();
    reader.onreading = event => {
      const records = [...event.message.records];
      setLast({
        tag: tagFromNdefRecords(records),
        serial: event.serialNumber || undefined,
        via: "NFC",
        raw: records.map(record => `${record.recordType}: ${decodeNdefRecord(record)}`),
      });
    };
    reader.onreadingerror = () => setError("readError");
    try {
      await reader.scan({ signal: controller.signal });
      setScanning(true);
    } catch (e) {
      setError(e instanceof Error ? e.name : "readError");
      setScanning(false);
    }
  }, [stop, supported]);

  /** Writes our tag as a text record. The phone must touch the tag while this runs. */
  const write = useCallback(async (tag: string) => {
    if (!window.NDEFReader) throw new Error("NotSupportedError");
    const writer = new window.NDEFReader();
    await writer.write({ records: [{ recordType: "text", data: tag }] }, { overwrite: true });
  }, []);

  useEffect(() => stop, [stop]);

  return { supported, scanning, start, stop, last, clear: () => setLast(undefined), error, write };
}
